import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail, Leaf, CheckCircle, Clock, RefreshCw, Eye, EyeOff, Lock, Check, X, AlertCircle } from "lucide-react"
import { Link } from "react-router-dom"
import { toast } from "sonner";
import { requestPasswordReset, resetPassword } from "@/utils/helper/auth";
import ForgotAuth from "@/assets/images/auth/forgot-auth.jpg"

export default function ForgotPassword() {
  const [currentStep, setCurrentStep] = useState("email") // email, otp, password, success, expired
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [timeLeft, setTimeLeft] = useState(600) // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false)
  // const [step, setStep] = useState("request");
  // const [formData, setFormData] = useState({
  //   email: "",
  //   otp: "",
  //   password: "",
  // });
  // const navigate = useNavigate();

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let strength = 0
    if (password.length >= 8) strength += 25
    if (/[A-Z]/.test(password)) strength += 25
    if (/[0-9]/.test(password)) strength += 25
    if (/[^A-Za-z0-9]/.test(password)) strength += 25
    return strength
  }

  const passwordStrength = getPasswordStrength(password)

  const getStrengthText = (strength) => {
    if (strength === 0) return ""
    if (strength <= 25) return "Weak"
    if (strength <= 50) return "Fair"
    if (strength <= 75) return "Good"
    return "Strong"
  }

  const getStrengthColor = (strength) => {
    if (strength <= 25) return "bg-red-500"
    if (strength <= 50) return "bg-yellow-500"
    if (strength <= 75) return "bg-blue-500"
    return "bg-green-500"
  }

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  // Timer effect - starts when OTP step is reached
  useEffect(() => {
    if (currentStep !== "otp") {
      return
    }

    // Reset timer when entering OTP step
    setTimeLeft(600)
    setCanResend(false)

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [currentStep])

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const data = await requestPasswordReset(email);

      if (data.success) {
        toast.success(data.message || "Verification code sent to your email.");
        setCurrentStep("otp");
        // startOtpTimer();
      } else {
        throw new Error(data.message || "Failed to send verification code.");
      }

    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to send verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      if (nextInput) nextInput.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const code = otp.join(""); // Combine the 6-digit OTP input

    try {
      if (code.length !== 6) {
        toast.error("Please enter the 6-digit code.");
        return;
      }

      // Optionally: you can verify OTP before password reset, or just move to next step
      setCurrentStep("password"); // 👈 Move to password step
    } catch {
      toast.error("Invalid or expired OTP");
      setCurrentStep("expired"); // 👈 Show expired state
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setIsLoading(true);

      const otpCode = otp.join(""); // Reuse the same OTP state

      await resetPassword({
        email,
        otp: otpCode,
        password,
      });

      toast.success("Password reset successful!");
      setCurrentStep("success"); // 👈 Show success state
    } catch (err) {
      const message = err.response?.data?.message || "Password reset failed";
      toast.error(message);

      if (message.toLowerCase().includes("expired") || message.toLowerCase().includes("invalid")) {
        setCurrentStep("expired");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true)
    try {
      const data = await requestPasswordReset(email)
      if (data.success) {
        toast.success(data.message || "Verification code resent to your email.")
        setTimeLeft(600)
        setCanResend(false)
        setOtp(["", "", "", "", "", ""])
      } else {
        throw new Error(data.message || "Failed to resend verification code.")
      }
    } catch (error) {
      console.error("Error:", error)
      toast.error(error.message || "Failed to resend verification code.")
    } finally {
      setIsLoading(false)
    }
  }

  const resetFlow = () => {
    setCurrentStep("email")
    setEmail("")
    setOtp(["", "", "", "", "", ""])
    setPassword("")
    setConfirmPassword("")
    setTimeLeft(600)
    setCanResend(false)
  }

  // Expired OTP State
  if (currentStep === "expired") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-primary mb-4">OTP Expired</h1>
            <p className="text-gray-600 mb-8">
              Your OTP has expired or is invalid. Please request a new one to reset your password.
            </p>
            <div className="space-y-4">
              <Button onClick={resetFlow} className="w-full bg-primary hover:bg-primary/90 text-white py-3">
                Request New OTP
              </Button>
              <Link to="/login" className="block text-gray-600 hover:text-primary font-medium">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success State
  if (currentStep === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Success Illustration */}
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src="https://placehold.co/500x600"
                alt="Password reset success"
                width={500}
                height={600}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-500/20 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Password Updated!</h2>
                <p className="text-white/90 max-w-sm">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
              </div>
            </div>
          </div>
          {/* Right Side - Success Message */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-4">Password Reset Successful!</h1>
              <p className="text-gray-600 mb-8">
                Your password has been successfully updated. You can now sign in to your account with your new password.
              </p>
              <div className="space-y-4">
                <Link to="/auth/login">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg">
                    Sign In to Your Account
                  </Button>
                </Link>
                <Link to="/" className="block text-gray-600 hover:text-primary font-medium">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // OTP Verification State
  if (currentStep === "otp") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - OTP Illustration */}
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src="https://placehold.co/500x600"
                alt="OTP verification illustration"
                width={500}
                height={600}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/20 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Check Your Email</h2>
                <p className="text-white/90 max-w-sm">
                  We've sent a 6-digit verification code to your email address. Enter it below to continue.
                </p>
              </div>
            </div>
          </div>
          {/* Right Side - OTP Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-primary">Fayoz</span>
                </div>
                <h1 className="text-2xl font-bold text-primary mb-2">Enter Verification Code</h1>
                <p className="text-gray-600 mb-2">We've sent a 6-digit code to:</p>
                <p className="font-semibold text-primary">{email}</p>
              </div>

              {/* OTP Form */}
              <form onSubmit={handleOtpSubmit} className="space-y-6">
                <div>
                  <Label className="text-primary font-medium mb-4 block text-center">Enter 6-Digit Code</Label>
                  <div className="flex gap-2 justify-center">
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-12 text-center text-lg font-bold border-gray-200 focus:border-secondary"
                        required
                      />
                    ))}
                  </div>
                </div>

                {/* Timer */}
                <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Clock className="w-5 h-5 text-secondary" />
                    <div className="text-left">
                      <p className="font-medium">
                        {timeLeft > 0 ? `Code expires in ${formatTime(timeLeft)}` : "Code expired"}
                      </p>
                      <p className="text-gray-600">Please check your spam folder if you don't see the email</p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || otp.join("").length !== 6}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Verifying Code...
                    </div>
                  ) : (
                    "Verify Code"
                  )}
                </Button>
              </form>

              {/* Resend Options */}
              <div className="text-center mt-6 space-y-4">
                {canResend || timeLeft === 0 ? (
                  <Button
                    variant="outline"
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent py-3 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {isLoading ? "Resending..." : "Resend Code"}
                  </Button>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Didn't receive the code? You can resend in {formatTime(timeLeft)}
                  </p>
                )}

                <button
                  type="button"
                  onClick={resetFlow}
                  className="text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  Change Email Address
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // New Password State
  if (currentStep === "password") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Password Illustration */}
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src="https://placehold.co/500x600"
                alt="Create new password illustration"
                width={500}
                height={600}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Create New Password</h2>
                <p className="text-white/90 max-w-sm">
                  Choose a strong password to keep your account secure and continue your skincare journey with
                  confidence.
                </p>
              </div>
            </div>
          </div>
          {/* Right Side - Password Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-primary">Fayoz</span>
                </div>
                <h1 className="text-2xl font-bold text-primary mb-2">Create New Password</h1>
                <p className="text-gray-600">Enter your new password below</p>
              </div>

              {/* Password Form */}
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="text-primary font-medium">
                    New Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                            style={{ width: `${passwordStrength}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 min-w-12">{getStrengthText(passwordStrength)}</span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div className="flex items-center gap-2">
                          {password.length >= 8 ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400" />
                          )}
                          <span>At least 8 characters</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/[A-Z]/.test(password) ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400" />
                          )}
                          <span>One uppercase letter</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/[0-9]/.test(password) ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400" />
                          )}
                          <span>One number</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/[^A-Za-z0-9]/.test(password) ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400" />
                          )}
                          <span>One special character</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-primary font-medium">
                    Confirm New Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      Passwords do not match
                    </p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-green-500 text-xs mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Passwords match
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || password !== confirmPassword || passwordStrength < 75}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Updating Password...
                    </div>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>

              {/* Security Note */}
              <div className="mt-6 p-4 bg-secondary/10 border border-secondary/20 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Security tip:</strong> Use a unique password that you don't use for other accounts. Consider
                  using a password manager for better security.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Initial Email Form State
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:block">
          <div className="relative">
            <img
            src={ForgotAuth}
              alt="Forgot password illustration"
              width={500}
              height={600}
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
            <div className="absolute bottom-8 left-8 text-white">
              <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Forgot Your Password?</h2>
              <p className="text-white/90 max-w-sm">
                No worries! It happens to the best of us. We'll send you a verification code to reset your password.
              </p>
            </div>
          </div>
        </div>
        {/* Right Side - Email Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-primary">Fayoz</span>
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">Reset Your Password</h1>
              <p className="text-gray-600">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
            </div>
            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-primary font-medium">
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    className="pl-10 py-3 border-gray-200 focus:border-secondary"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending Code...
                  </div>
                ) : (
                  "Send Verification Code"
                )}
              </Button>
            </form>
            {/* Help Text */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-primary mb-2">Need help?</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Make sure you enter the email associated with your account</li>
                <li>• Check your spam/junk folder</li>
                <li>• The verification code will expire in 10 minutes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
